import { useEffect, useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';

/**
 * As peças do menu lateral recolhido — só os ícones, com o nome numa dica.
 *
 * ## A dica espera 1 segundo
 *
 * Com o mouse parado sobre o ícone por 1 segundo, o nome aparece à direita. A
 * espera é de propósito: passar o mouse pelo menu a caminho do conteúdo não
 * acende uma fila de balões. Pelo teclado (Tab), a dica aparece na hora — quem
 * navega assim não está "passando por cima", está escolhendo.
 *
 * O `title` nativo não serve: o atraso é do navegador (e muda entre eles), e
 * ele não aparece no foco do teclado.
 */

/** Quanto o mouse fica parado antes de o nome aparecer. */
const ESPERA_DA_DICA_MS = 1000;

/** A dica fica à direita do ícone, presa à janela — o menu rola e corta o que sai dele. */
export function DicaDoMenu(props: {
  readonly texto: string;
  /** Menu aberto: o nome já está escrito ao lado do ícone, e a dica sobraria. */
  readonly desligada?: boolean;
  readonly children: ReactNode;
}): JSX.Element {
  const ancora = useRef<HTMLDivElement>(null);
  const relogio = useRef<number | null>(null);
  const [posicao, setPosicao] = useState<{ top: number; left: number } | null>(null);

  const cancelar = (): void => {
    if (relogio.current !== null) window.clearTimeout(relogio.current);
    relogio.current = null;
  };
  const mostrar = (): void => {
    const r = ancora.current?.getBoundingClientRect();
    if (r !== undefined) setPosicao({ top: r.top + r.height / 2, left: r.right + 10 });
  };
  const esconder = (): void => {
    cancelar();
    setPosicao(null);
  };

  // Desmontar com o relógio correndo não pode acender uma dica depois.
  useEffect(() => cancelar, []);

  // Rolar o menu ou mudar a janela desalinharia a dica: ela some.
  useEffect(() => {
    if (posicao === null) return;
    const sumir = (): void => setPosicao(null);
    window.addEventListener('scroll', sumir, true);
    window.addEventListener('resize', sumir);
    return () => {
      window.removeEventListener('scroll', sumir, true);
      window.removeEventListener('resize', sumir);
    };
  }, [posicao]);

  if (props.desligada === true) return <>{props.children}</>;

  return (
    <div
      ref={ancora}
      onMouseEnter={() => {
        cancelar();
        relogio.current = window.setTimeout(mostrar, ESPERA_DA_DICA_MS);
      }}
      onMouseLeave={esconder}
      onFocus={(evento) => {
        // Só o foco do teclado: o clique também foca, e aí a dica sobraria.
        if (evento.target instanceof HTMLElement && evento.target.matches(':focus-visible')) {
          mostrar();
        }
      }}
      onBlur={esconder}
      onClick={esconder}
    >
      {props.children}
      {posicao !== null &&
        createPortal(
          // O nome já está no link (texto para leitor de tela): a dica é só visual.
          <div
            aria-hidden="true"
            style={{
              position: 'fixed',
              top: posicao.top,
              left: posicao.left,
              transform: 'translateY(-50%)',
              zIndex: 70,
            }}
            className="pointer-events-none whitespace-nowrap rounded-card border border-borda bg-superficie-elevada px-2.5 py-1.5 text-xs font-medium text-conteudo-primario shadow-cartao"
          >
            {props.texto}
          </div>,
          document.body,
        )}
    </div>
  );
}

/** Tela de computador (`md` do Tailwind): só nela o menu recolhe — no celular ele é gaveta. */
const TELA_LARGA = '(min-width: 768px)';

export function useTelaLarga(): boolean {
  const [larga, setLarga] = useState(() => window.matchMedia(TELA_LARGA).matches);
  useEffect(() => {
    const consulta = window.matchMedia(TELA_LARGA);
    const aoMudar = (): void => setLarga(consulta.matches);
    consulta.addEventListener('change', aoMudar);
    return () => consulta.removeEventListener('change', aoMudar);
  }, []);
  return larga;
}

/** A preferência fica neste navegador: quem recolheu o menu o encontra recolhido. */
const CHAVE_RECOLHIDO = 'deca.menu.recolhido';

export function lerRecolhido(): boolean {
  try {
    return window.localStorage.getItem(CHAVE_RECOLHIDO) === '1';
  } catch {
    return false;
  }
}

export function gravarRecolhido(recolhido: boolean): void {
  try {
    if (recolhido) window.localStorage.setItem(CHAVE_RECOLHIDO, '1');
    else window.localStorage.removeItem(CHAVE_RECOLHIDO);
  } catch {
    // Sem armazenamento, a escolha vale até recarregar — nada quebra.
  }
}

/** "Gabriel Marquezi Santos" → "GS". */
export function iniciaisDe(nome: string): string {
  const partes = nome.trim().split(/\s+/).filter(Boolean);
  const primeira = partes[0]?.[0] ?? '';
  const ultima = partes.length > 1 ? (partes[partes.length - 1]?.[0] ?? '') : '';
  return `${primeira}${ultima}`.toUpperCase() || '?';
}
